// Alma Capital ERP — الهيكل: قائمة جانبية (مجموعات: الموقع الإلكتروني، الالتزام والحوكمة، السوشل ميديا، النظام) + صفحات مستقلة لكل قسم.
// الوصول: الأدمن يرى الكل؛ غيره يرى الأقسام الممنوحة له (users.sections) — والتعديل بدور manager/admin.
import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ALL_ITEMS, LEGACY_TABS } from "./nav";
import Sidebar from "./components/Sidebar";
import LoginPage from "./pages/LoginPage";
import AlmaSection from "./pages/AlmaCapitalPage";
import UsersPage from "./pages/UsersPage";
import AuditPage from "./pages/AuditPage";
import SocialPage from "./pages/SocialPage";

function Shell({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`shell${open ? " sb-open" : ""}`}>
      <Sidebar onClose={() => setOpen(false)} />
      <div className="sb-backdrop" onClick={() => setOpen(false)} />
      <div className="shell-main">
        <header className="topbar"><button type="button" onClick={() => setOpen(true)} aria-label="القائمة">☰</button><div className="t">🏛️ ألما المالية — نظام الإدارة</div></header>
        <main className="shell-content">{children}</main>
      </div>
    </div>
  );
}

// أول مسار مسموح للمستخدم (للتحويل من الجذر أو من قسم غير ممنوح)
const useFirstAllowed = () => {
  const { isAdmin, canSee } = useAuth();
  const it = ALL_ITEMS.find((x) => (x.admin ? isAdmin : canSee(x.key)));
  return it ? it.path : null;
};

function Guard({ children, section, admin }) {
  const { user, loading, isAdmin, canSee } = useAuth();
  const first = useFirstAllowed();
  const { pathname } = useLocation();
  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جارٍ التحميل…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = admin ? isAdmin : section ? canSee(section) : true;
  if (!allowed) return first && first !== pathname ? <Navigate to={first} replace /> : <Shell><NoAccess /></Shell>;
  return <Shell>{children}</Shell>;
}

function NoAccess() {
  return <div className="page"><div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 30, textAlign: "center", color: "#64748b", fontWeight: 700 }}>🏛️ لم تُمنح لك أي أقسام في هذا النظام. اطلب من مدير النظام تفعيل الأقسام المطلوبة من إدارة المستخدمين.</div></div>;
}

// الجذر: روابط قديمة (/?tab=x) تُحوَّل إلى المسار الجديد، وإلا أول قسم مسموح
function Home() {
  const { user, loading } = useAuth();
  const first = useFirstAllowed();
  const [sp] = useSearchParams();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const legacy = LEGACY_TABS[sp.get("tab")];
  if (legacy) return <Navigate to={legacy} replace />;
  return first ? <Navigate to={first} replace /> : <Shell><NoAccess /></Shell>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Home />} />
          <Route path="/site/content" element={<Guard section="content"><AlmaSection section="content" /></Guard>} />
          <Route path="/site/messages" element={<Guard section="messages"><AlmaSection section="messages" /></Guard>} />
          <Route path="/site/settings" element={<Guard section="settings"><AlmaSection section="settings" /></Guard>} />
          <Route path="/compliance" element={<Guard section="compliance"><AlmaSection section="compliance" /></Guard>} />
          <Route path="/people" element={<Guard section="people"><AlmaSection section="people" /></Guard>} />
          <Route path="/startup" element={<Guard section="startup"><AlmaSection section="startup" /></Guard>} />
          <Route path="/social" element={<Guard section="social"><SocialPage /></Guard>} />
          <Route path="/system/users" element={<Guard admin><UsersPage /></Guard>} />
          <Route path="/system/audit" element={<Guard admin><AuditPage /></Guard>} />
          <Route path="/users" element={<Navigate to="/system/users" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
